import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  Typography,
  TextField,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Pagination,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';

export function DataTable({
  data,
  columns,
  filters = [],
  title,
  onAdd,
  addButtonText = "Add New",
  rowsPerPageOptions = [5, 10, 25],
  defaultRowsPerPage = 10,
  totalPages,
  currentPage,
  selectable = false,
  onSelectionChange,
  onSelect,
  onRowClick,
  onSearch,
  loading = false,
  emptyMessage = "No data available",
  className,
  initialFilterValues = {},
}) {
  // State management
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');
  // Initialize page from currentPage prop if provided, otherwise default to 1
  const [page, setPage] = useState(() => currentPage ?? 1); // Material-UI Pagination is 1-indexed
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterValues, setFilterValues] = useState(initialFilterValues);
  const [selectedRows, setSelectedRows] = useState([]);

  // Sync internal page state with parent's currentPage prop when it changes from parent
  // Use a ref to track if this is the first render and to prevent unnecessary updates
  const isFirstRender = React.useRef(true);
  const lastSyncedPage = React.useRef(currentPage ?? 1);
  
  useEffect(() => {
    // Skip sync on first render (initial state is already set correctly)
    if (isFirstRender.current) {
      isFirstRender.current = false;
      lastSyncedPage.current = currentPage ?? page;
      return;
    }
    
    // Only sync if currentPage is provided, has actually changed, and is different from current internal state
    // This prevents the effect from interfering with user-initiated page changes
    if (currentPage !== undefined && currentPage !== lastSyncedPage.current && currentPage !== page) {
      setPage(currentPage);
      lastSyncedPage.current = currentPage;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]); // Only depend on currentPage to avoid circular updates

  // Ensure columns is treated as an array
  const columnsArray = Array.isArray(columns) ? columns : [];

  // Handle search
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setPage(1); // Reset to page 1 (1-indexed)
    
    // Trigger search with new search term
    if (onSearch) {
      onSearch({
        searchTerm: value,
        filterValues: filterValues,
        page: 1, // Pass 1-indexed page to parent
        rowsPerPage,
        orderBy,
        order,
      });
    }
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    // Material-UI Pagination is 1-indexed, keep it 1-indexed
    setPage(newPage);
    
    // Trigger search with new page (pass 1-indexed page to parent)
    if (onSearch) {
      onSearch({
        searchTerm: searchTerm,
        filterValues: filterValues,
        page: newPage, // Pass 1-indexed page to parent
        rowsPerPage,
        orderBy,
        order,
      });
    }
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(1); // Reset to page 1 (1-indexed)
    
    // Trigger search with new rows per page
    if (onSearch) {
      onSearch({
        searchTerm: searchTerm,
        filterValues: filterValues,
        page: 1, // Pass 1-indexed page to parent
        rowsPerPage: newRowsPerPage,
        orderBy,
        order,
      });
    }
  };

  // Handle filtering
  const handleFilterChange = (filterKey, value) => {
    const newFilterValues = { ...filterValues, [filterKey]: value };
    setFilterValues(newFilterValues);
    setPage(1); // Reset to page 1 (1-indexed)
    
    // If this is a search filter, update searchTerm as well
    if (filterKey === 'search') {
      setSearchTerm(value);
    }
    
    // Trigger search with new filter values
    if (onSearch) {
      onSearch({
        searchTerm: filterKey === 'search' ? value : searchTerm,
        filterValues: newFilterValues,
        page: 1, // Pass 1-indexed page to parent
        rowsPerPage,
        orderBy,
        order,
      });
    }
  };

  // Handle sorting
  const handleRequestSort = (property) => {
    const newOrder = orderBy === property && order === 'asc' ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(property);
    
    // Trigger search with new sorting
    if (onSearch) {
      onSearch({
        searchTerm: searchTerm,
        filterValues: filterValues,
        page: page,
        rowsPerPage,
        orderBy: property,
        order: newOrder,
      });
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setFilterValues({});
    setPage(1); // Reset to page 1 (1-indexed)
    
    // Trigger search with cleared filters
    if (onSearch) {
      onSearch({
        searchTerm: '',
        filterValues: {},
        page: 1, // Pass 1-indexed page to parent
        rowsPerPage,
        orderBy,
        order,
      });
    }
  };

  // Filter and sort data
  const filteredData = useMemo(() => {
    return data;
  }, [data]);

  const sortedData = useMemo(() => {
    if (!orderBy) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return order === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return order === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      return 0;
    });
  }, [filteredData, order, orderBy]);

  // Convert 1-indexed page to 0-indexed for array slicing
  // If totalPages is provided, we're using server-side pagination, so don't slice the data
  // The data prop already contains the correct page of results from the server
  // If totalPages is not provided, use client-side pagination
  const paginatedData = totalPages !== undefined && totalPages > 0
    ? sortedData // Server-side pagination: use data as-is
    : sortedData.slice( // Client-side pagination: slice the data
        (page - 1) * rowsPerPage,
        (page - 1) * rowsPerPage + rowsPerPage
      );

  // Handle row selection
  const handleRowSelect = (row) => {
    const isSelected = selectedRows.some(selected => selected.id === row.id);
    let newSelection;
    
    if (isSelected) {
      newSelection = selectedRows.filter(selected => selected.id !== row.id);
    } else {
      newSelection = [...selectedRows, row];
    }
    
    setSelectedRows(newSelection);
    onSelectionChange?.(newSelection);
  };

  // Render cell content
  const renderCell = (column, row) => {
    const value = row[column.id];
    
    if (column.render) {
      return column.render(value, row);
    }
    
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    
    if (typeof value === 'number') {
      return value.toLocaleString();
    }
    
    return value?.toString() || '';
  };

  // Check if filters are active
  const hasActiveFilters = searchTerm || Object.values(filterValues).some(v => v && v !== 'all');

  return (
    <Paper sx={{ p: 3 }} className={className}>
      {/* Header */}
      {(title || onAdd) && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          {title && <Typography variant="h5">{title}</Typography>}
          {onAdd && (
            <Button variant="contained" onClick={onAdd}>
              {addButtonText}
            </Button>
          )}
        </Box>
      )}

      {/* Filters */}
      {(filters.length > 0) && (
        <>
          {/* Desktop filters */}
          <Box sx={{ 
            display: { xs: 'none', sm: 'grid' }, 
            gridTemplateColumns: { sm: '1fr 1fr', md: `${filters.length > 0 ? '2fr' : '1fr'} 1fr 1fr 1fr` }, 
            gap: 2, 
            mb: 3 
          }}>
            {filters.map((filter) => (
              <FormControl key={filter.key} fullWidth>
                {filter.type === 'text' ? (
                  <TextField
                    fullWidth
                    label={filter.label}
                    placeholder={filter.placeholder || `Search ${filter.label.toLowerCase()}...`}
                    value={filter.key === 'search' ? searchTerm : (filterValues[filter.key] || '')}
                    onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                ) : filter.type === 'select' ? (
                  <>
                    <InputLabel>{filter.label}</InputLabel>
                    <Select
                      value={filterValues[filter.key] || 'all'}
                      label={filter.label}
                      onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                    >
                      <MenuItem value="all">
                        All {filter.label === 'Status' ? 'Statuses' : filter.label === 'Category' ? 'Categories' : `${filter.label}s`}
                      </MenuItem>
                      {filter.options?.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </>
                ) : (
                  <TextField
                    fullWidth
                    label={filter.label}
                    type="date"
                    value={filterValues[filter.key] || ''}
                    onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              </FormControl>
            ))}
            
            <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={clearFilters}
                fullWidth
                sx={{
                  '&:hover': {
                    borderColor: 'error.dark',
                    backgroundColor: 'error.light',
                    color: 'error.contrastText'
                  }
                }}
              >
                Clear Filters
            </Button>
          </Box>

          {/* Mobile filters */}
          <Box sx={{ display: { xs: 'block', sm: 'none' }, mb: 2 }}>
            <Stack spacing={2}>
              {filters.map((filter) => (
                <FormControl key={filter.key} fullWidth>
                  {filter.type === 'text' ? (
                    <TextField
                      fullWidth
                      label={filter.label}
                      placeholder={filter.placeholder || `Search ${filter.label.toLowerCase()}...`}
                      value={filter.key === 'search' ? searchTerm : (filterValues[filter.key] || '')}
                      onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                    />
                  ) : filter.type === 'select' ? (
                    <>
                      <InputLabel>{filter.label}</InputLabel>
                      <Select
                        value={filterValues[filter.key] || 'all'}
                        label={filter.label}
                        onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                      >
                        <MenuItem value="all">All {filter.label}s</MenuItem>
                        {filter.options?.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </>
                  ) : (
                    <TextField
                      fullWidth
                      label={filter.label}
                      type="date"
                      value={filterValues[filter.key] || ''}
                      onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                </FormControl>
              ))}
              
                <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={clearFilters}
                    fullWidth
                    sx={{
                    '&:hover': {
                        borderColor: 'error.dark',
                        backgroundColor: 'error.light',
                        color: 'error.contrastText'
                    }
                    }}
                >
                    Clear Filters
                </Button>
            </Stack>
          </Box>
        </>
      )}

      <Pagination 
        count={totalPages || 0}
        page={page || 1}
        color="primary"
        onChange={handleChangePage}
        sx={{ display: { xs: 'block', sm: 'none' } }}
      />

      {/* Desktop Table */}
      <Box sx={{ 
        overflowX: 'auto', 
        WebkitOverflowScrolling: 'touch', 
        display: { xs: 'none', sm: 'block' },
        mb: 2
      }}>
        <TableContainer sx={{ minWidth: { xs: 600, sm: 'auto' } }}>
          <Table>
            <TableHead>
              <TableRow>
                {columnsArray.map((column) => (
                  <TableCell
                    key={column.id.toString()}
                    sortDirection={orderBy === column.id ? order : false}
                    sx={{
                      width: column.width,
                      display: {
                        xs: column.id === columnsArray[0]?.id ? 'table-cell' : 'none',
                        sm: 'table-cell'
                      }
                    }}
                  >
                    {column.sortable ? (
                      <TableSortLabel
                        active={orderBy === column.id}
                        direction={orderBy === column.id ? order : 'asc'}
                        onClick={() => handleRequestSort(column.id.toString())}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((row) => (
                <TableRow 
                  key={row.id} 
                  hover
                  selected={selectable && selectedRows.some(selected => selected.id === row.id)}
                  onClick={() => {
                    if (onRowClick) {
                      onRowClick(row);
                    }
                    if (onSelect) {
                      onSelect(row);
                    }
                    if (selectable) {
                      handleRowSelect(row);
                    }
                  }}
                  sx={{ cursor: onRowClick || onSelect || selectable ? 'pointer' : 'default' }}
                >
                  {columnsArray.map((column) => (
                    <TableCell
                      key={column.id.toString()}
                      sx={{
                        width: column.width,
                        display: {
                          xs: column.id === columnsArray[0]?.id ? 'table-cell' : 'none',
                          sm: 'table-cell'
                        }
                      }}
                    >
                      {renderCell(column, row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Mobile Card View */}
      <Box sx={{ display: { xs: 'block', sm: 'none' }, mt: 2, mb: 2 }}>
        <Stack spacing={2}>
          {paginatedData.map((row) => (
            <Card 
              key={row.id} 
              sx={{ 
                p: 2, 
                cursor: onRowClick || onSelect ? 'pointer' : 'default',
                '&:hover': onRowClick || onSelect ? { 
                  boxShadow: 3, 
                  transform: 'translateY(-1px)',
                  transition: 'all 0.2s ease-in-out'
                } : {}
              }}
              onClick={() => {
                if (onRowClick) {
                  onRowClick(row);
                }
                if (onSelect) {
                  onSelect(row);
                }
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 'medium', mb: 2 }}>
                {columnsArray[0] ? renderCell(columnsArray[0], row) : ''}
              </Typography>
              
              {columnsArray.slice(1).map((column) => (
                <Box key={column.id.toString()} sx={{ mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {column.label}:
                  </Typography>
                  {column.render ? (
                    column.render(row[column.id], row)
                  ) : (
                    <Typography variant="body2">
                      {renderCell(column, row)}
                    </Typography>
                  )}
                </Box>
              ))}
            </Card>
          ))}
        </Stack>
      </Box>

      {/* Empty state */}
      {totalPages === 0 && !loading && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            {hasActiveFilters ? 'No results match your filters' : emptyMessage}
          </Typography>
          {hasActiveFilters && (
            <Button onClick={clearFilters} sx={{ mt: 2 }}>
              Clear Filters
            </Button>
          )}
        </Box>
      )}

      {/* Loading state */}
      {loading && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            Loading...
          </Typography>
        </Box>
      )}

        <Pagination 
            count={totalPages || 0}
            page={page || 1} // Material-UI Pagination is 1-indexed
            color="primary"
            onChange={handleChangePage}
        />
    </Paper>
  );
}

export default DataTable;
